import { resourceRecovery } from "./resourceRecovery.ts";
import { ApiError, req } from "../api.ts";
import { esc, h } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { topNav } from "../ui/topnav.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { copyText } from "../ui/clipboard.ts";
import { confirmDialog } from "../ui/confirm.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { t } from "../i18n/index.ts";
import { Chessground } from "chessground";
import { commandId, ensurePlayer, PlayConnection, PlayJournal, readPlayer, outcomeMessage, sessionRecovery, type Slot } from "../play/client.ts";
import { acceptLiveOffer, LiveOfferConsent, takeLiveOffer } from "../play/offerConsent.ts";
import { clockLabel, clockPicker, controlTerms, pickedControl, termsLabel, type Terms } from "../play/clock.ts";

export function playPage(content: string, active: "play" | "players" | "games" | null = "play"): HTMLElement {
  const root = h(`<div class="club-page"><main class="club-content">${content}</main></div>`);
  root.prepend(topNav(active)); return root;
}

/** The phone handshake: brand and a way back, one sentence, the one current
 * button, then the position, then everything else. The primary button sticks
 * above the home indicator, so no status line can push it out of reach. */
export function handshakePage(): HTMLElement {
  return h(`<div class="club-page handshake"><header class="topnav hs-nav"><a class="brand" data-link href="/" aria-label="${BRAND_NAME}">${brandLockup}</a><a class="hs-back" data-link href="/">${t("hs.back")}</a></header>
    <main class="club-content"><h1 class="hs-line" data-line></h1><p class="hs-status" data-status role="status" aria-live="polite"></p>
      <button class="btn primary hs-primary" data-primary></button><div class="hs-quiet" data-quiet></div>
      <div class="position-preview cg-board-wrap" hidden></div><div class="hs-more" data-more></div>
      <p class="msg" role="status" aria-live="polite"></p></main></div>`);
}
/** "Alex is here. You have white. 5+3." */
export function handshakeLine(who: string, side: "white" | "black", terms: Terms | undefined): string {
  return `${who} ${t(side === "white" ? "hs.sideWhite" : "hs.sideBlack")} ${clockLabel(terms)}.`;
}
export function quietAction(label: string, run: () => void): HTMLButtonElement {
  const button = document.createElement("button"); button.type = "button"; button.className = "link-button"; button.textContent = label;
  button.onclick = run; return button;
}

const failure = (error: unknown) => error instanceof Error ? error.message : "Could not connect. Please retry.";
const lastSlot = "chessme.last-slot.v1";
const invitationKey = (token: string) => `chessme.invite.v1:${token}`;
// A first yes was given on this slot in this tab. Coming back asks "I'm here".
const pressedKey = (slot: string) => `chessme.handshake.v1:${slot}`;
function pressed(slot: string): boolean { try { return sessionStorage.getItem(pressedKey(slot)) === "1"; } catch { return false; } }
function press(slot: string): void { try { sessionStorage.setItem(pressedKey(slot), "1"); } catch { /* the button label only */ } }
function rememberInvitation(token: string, slot: string): void {
  sessionStorage.setItem(invitationKey(token), slot);
  // Messages may reopen the link in a new Safari tab. This is a navigation hint;
  // GET /slots still requires the same participant's browser cookie.
  try { localStorage.setItem(invitationKey(token), slot); } catch { /* this tab can still return */ }
}
function remember(slot: Slot): void {
  sessionStorage.setItem(lastSlot, slot.id);
  // A normal browser may open a new tab when returning from the native app.
  // This is only a navigation hint; the server still checks the current SID.
  try { localStorage.setItem(lastSlot, slot.id); } catch { /* the current tab can still return */ }
  if (slot.invite_url) rememberInvitation(slot.invite_url.split("/").at(-1)!, slot.id);
}
function mounted(root: HTMLElement, run: () => () => void): void {
  requestAnimationFrame(() => { if (root.isConnected) onDisconnect(root,run()); });
}
export function review(root: HTMLElement, setup: Slot["setup"], color: "white" | "black"): () => void {
  const fen = setup?.transfer?.snapshot.final_fen;
  const holder = root.querySelector<HTMLElement>(".position-preview")!;
  if (!fen) { holder.hidden = true; holder.replaceChildren(); return () => {}; }
  holder.hidden = false;
  const board = Chessground(holder,{fen,orientation:color,viewOnly:true,coordinates:true});
  return () => board.destroy();
}
const expiry = (at: number) => t("hs.expires", { when: new Date(at*1000).toLocaleString() });

export async function playScreen(): Promise<HTMLElement> {
  let prior: string | null = null;
  try { prior = sessionStorage.getItem(lastSlot) ?? localStorage.getItem(lastSlot); } catch { /* explicit action will explain storage needs */ }
  if (prior && /^[A-Za-z0-9_-]{16,64}$/.test(prior)) {
    try {
      // Cookies may have been cleared or switched since this hint was stored.
      // Never bootstrap a guest, expose another player's slot, or create a new
      // invitation merely because Play was opened in a fresh tab.
      await req<Slot>("GET",`/api/play/slots/${prior}`);
    } catch { prior = null; }
  }
  const root = playPage(`<p class="eyebrow">${t("play.eyebrow")}</p><h1>${t("play.title")}</h1>
    <p class="club-intro">${t("play.intro")}</p>
    <section class="play-choices"><div class="club-panel"><h2>${t("play.invite")}</h2><p>${t("play.inviteHelp")}</p>
      <label class="field"><span>${t("play.side")}</span><select name="color"><option value="white">${t("chess.white")}</option><option value="black">${t("chess.black")}</option></select></label>
      ${clockPicker()}
      <button class="btn primary" data-create>${t("play.create")}</button><p class="fine" data-terms></p></div>
    <div class="club-panel"><h2>${t("play.find")}</h2><p>${t("play.findHelp")}</p><a class="btn" data-link href="/club">${t("play.find")}</a></div></section>
    <p class="msg" role="status" aria-live="polite"></p>
    <section class="club-panel open-invitations" data-invitations hidden aria-labelledby="open-invitations-title">
      <h2 id="open-invitations-title">${t("play.openInvitations")}</h2><div data-invitation-list></div>
      <p class="fine" data-invitation-status role="status"></p><button class="btn" data-refresh-invitations hidden>${t("common.retry")}</button></section>
    ${prior && /^[A-Za-z0-9_-]{16,64}$/.test(prior) ? `<p><a data-link href="/play/slot/${esc(prior)}">${t("play.prior")}</a></p>` : ""}
    <p class="study-line">${t("play.studyLine")} <a data-link href="/study">${t("play.studyLink")}</a></p>
    <p><a class="btn ghost" data-link href="/play/computer">${t("computer.title")}</a></p>
    <p class="fine">${t("play.noAccount")}</p>`);
  const terms = root.querySelector<HTMLElement>("[data-terms]")!;
  const paintTerms = () => {
    const control = pickedControl(root);
    terms.textContent = termsLabel(control === "untimed" ? {clock_mode:"untimed"} : {clock_mode:"timed",tc:control,speed:control === "10+0" ? "rapid" : "blitz"});
  };
  root.querySelector(".clock-picker")!.addEventListener("change", paintTerms); paintTerms();
  const button = root.querySelector<HTMLButtonElement>("[data-create]")!;
  const message = root.querySelector<HTMLElement>(".msg")!;
  const invitations = root.querySelector<HTMLElement>("[data-invitations]")!;
  const list = root.querySelector<HTMLElement>("[data-invitation-list]")!;
  const listStatus = root.querySelector<HTMLElement>("[data-invitation-status]")!;
  const retryList = root.querySelector<HTMLButtonElement>("[data-refresh-invitations]")!;
  let refreshVersion = 0;
  async function refreshInvitations() {
    const version = ++refreshVersion;
    retryList.disabled = true;
    try {
      // Visiting Play only reads the existing session; creation remains explicit.
      const player = await readPlayer();
      const slots = player ? (await req<{slots: Slot[]}>("GET", "/api/play/slots")).slots : [];
      if (version !== refreshVersion) return;
      list.replaceChildren(); listStatus.textContent = ""; retryList.hidden = true;
      invitations.hidden = slots.length === 0;
      for (const slot of slots) {
        const title = slot.recipient ? t("play.invitationWith", {name: slot.recipient.name}) : t("play.waitingForFriend");
        const row = h(`<article class="club-row open-invitation" data-slot="${esc(slot.id)}"><div><strong>${esc(title)}</strong>
          <p class="fine">${esc(termsLabel(slot.terms))}</p><p class="fine">${esc(expiry(slot.expires_at))}</p></div>
          <div class="club-actions"><a class="btn" data-link href="/play/slot/${esc(slot.id)}">${t("play.openInvitation")}</a>
          ${slot.kind !== "match" ? `<button class="link-button" data-cancel-invitation>${t("play.cancelInvitation")}</button>` : ""}</div></article>`);
        const cancel = row.querySelector<HTMLButtonElement>("[data-cancel-invitation]");
        if (cancel) cancel.onclick = async () => {
          cancel.disabled = true;
          try {
            if (!await confirmDialog(t("hs.cancelConfirm"), {danger:true, confirmLabel:t("play.cancelInvitation")}) || !root.isConnected) return;
            const result = await new PlayJournal(slot.player_id, `slot:${slot.id}`).send(`/api/play/slots/${slot.id}/commands`, {
              actor_id:slot.player_id, command_id:commandId(), expected_revision:slot.revision, action:{type:"cancel"},
            });
            message.textContent = result.outcome === "applied" && result.slot?.state === "cancelled"
              ? t("play.invitationCancelled") : outcomeMessage(result.code ?? "stale_revision");
            await refreshInvitations();
          } catch (error) { message.textContent = failure(error); sessionRecovery(root,error); }
          finally { cancel.disabled = false; }
        };
        list.append(row);
      }
    } catch (error) {
      if (version !== refreshVersion) return;
      list.replaceChildren(); invitations.hidden = false; retryList.hidden = false;
      listStatus.textContent = t("play.invitationLoadFailed"); sessionRecovery(root,error);
    } finally { if (version === refreshVersion) retryList.disabled = false; }
  }
  retryList.onclick = () => void refreshInvitations();
  void refreshInvitations();
  mounted(root, () => {
    const refresh = () => { if (document.visibilityState === "visible") void refreshInvitations(); };
    window.addEventListener("focus", refresh);
    return () => { ++refreshVersion; window.removeEventListener("focus", refresh); };
  });
  async function createInvitation() {
    if (button.disabled || !root.isConnected) return;
    button.disabled = true;
    message.textContent = t("play.preparing");
    try {
      const player = await ensurePlayer();
      const color = root.querySelector<HTMLSelectElement>("select")!.value;
      // The control is inside the creation command, so an exact retry cannot swap it.
      const setup = {kind:"fresh",color,...controlTerms(pickedControl(root)),rated:false};
      const result = await new PlayJournal(player.id,"create-slot").send("/api/play/slots",{actor_id:player.id,command_id:commandId(),setup}, r => { if (r.slot) remember(r.slot); });
      if (result.outcome === "rejected" || !result.slot) throw new Error(outcomeMessage(result.code));
      navigate(`/play/slot/${result.slot.id}`);
    } catch (error) { message.textContent = failure(error); button.textContent = t("play.retryInvitation"); sessionRecovery(root,error,createInvitation); await refreshInvitations(); }
    finally { button.disabled = false; }
  }
  button.onclick = () => void createInvitation();
  return root;
}

interface Preview { id:string; revision:number; state:string; host_name:string; host_color:"white"|"black"; setup:Slot["setup"]; expires_at:number; kind:string; snapshot_digest:string; terms?: Terms }
export async function playInvitationScreen(params: Params): Promise<HTMLElement> {
  const token = params.token;
  try {
    const known = sessionStorage.getItem(invitationKey(token)) ?? localStorage.getItem(invitationKey(token));
    if (known && /^[A-Za-z0-9_-]{16,64}$/.test(known)) {
      await req<Slot>("GET",`/api/play/slots/${known}`); navigate(`/play/slot/${known}`);
      return playPage("<p>Opening your invitation…</p>");
    }
  } catch { /* A remembered preview without a claim has no participant authority. */ }
  let preview: Preview;
  try { preview = await req<Preview>("GET",`/api/play/invitations/${encodeURIComponent(token)}`); }
  catch (error) {
    return playPage(`<h1>Invitation unavailable</h1><p>${esc(failure(error))}</p><p>If you already joined, use the original browser or recover your saved games.</p><a class="btn" data-link href="/my-games">My games</a><a class="btn ghost" data-link href="/">Create a new invitation</a>`);
  }
  const color = preview.host_color === "white" ? "black" : "white";
  const imported = preview.kind === "import";
  const root = handshakePage();
  if (imported) root.classList.add("live-offer");
  const primary = root.querySelector<HTMLButtonElement>("[data-primary]")!;
  const more = root.querySelector<HTMLElement>("[data-more]")!;
  const message = root.querySelector<HTMLElement>(".msg")!;
  primary.textContent = t("hs.illPlay");
  root.querySelector("[data-status]")!.textContent = termsLabel(preview.terms);
  const notes = (items: string[]) => more.replaceChildren(...items.map(text => { const p = document.createElement("p"); p.className = "fine"; p.textContent = text; return p; }));
  if (preview.state === "waiting_for_host") {
    // The preview never claims a seat: I'll play waits for the host's browser.
    root.querySelector("[data-line]")!.textContent = handshakeLine(t("hs.hostOpening"), color, preview.terms);
    primary.disabled = true;
    notes([t("hs.keepMessages"), t("hs.messagesEnds"), expiry(preview.expires_at)]);
    mounted(root, () => {
      const destroy = review(root, preview.setup, color);
      let stopped = false, timer: ReturnType<typeof setTimeout>;
      const refresh = async () => {
        if (stopped) return;
        try {
          if (document.visibilityState === "visible") {
            const current = await req<Preview>("GET", `/api/play/invitations/${encodeURIComponent(token)}`);
            if (stopped) return;
            if (current.state !== "waiting_for_host") { navigate(`/play/invite/${token}`); return; }
          }
        } catch (error) {
          if (stopped) return;
          message.textContent = failure(error);
          if (error instanceof ApiError && [404,409].includes(error.status)) return;
        }
        timer = setTimeout(() => void refresh(), 2000);
      };
      timer = setTimeout(() => void refresh(), 1500);
      return () => { stopped = true; clearTimeout(timer); destroy(); };
    });
    return root;
  }
  root.querySelector("[data-line]")!.textContent = handshakeLine(t(imported ? "hs.offerFrom" : "hs.invitedBy", {name: preview.host_name}), color, preview.terms);
  notes([...(imported ? [t("hs.messagesEnds")] : []), `${t("hs.noApp")} ${expiry(preview.expires_at)}`]);
  mounted(root,() => review(root,preview.setup,color));
  primary.onclick = async () => {
    primary.disabled = true;
    let stayedVisible = document.visibilityState === "visible";
    const visibility = () => { if (document.visibilityState !== "visible") stayedVisible = false; };
    document.addEventListener("visibilitychange", visibility);
    try {
      const player = await ensurePlayer();
      rememberInvitation(token, preview.id);
      const result = await new PlayJournal(player.id,`claim:${preview.id}`).send(`/api/play/slots/${preview.id}/commands`,{
        actor_id:player.id,command_id:commandId(),expected_revision:preview.revision,action:{type:"claim",invite_token:token},
      },r => { if (r.slot) remember(r.slot); });
      if (result.code === "self_claim" && result.slot) { navigate(`/play/slot/${result.slot.id}`); return; }
      if (result.outcome === "rejected" || !result.slot) throw new Error(outcomeMessage(result.code));
      if (!root.isConnected) return;
      press(result.slot.id);
      // One press: this claim also carries readiness while the page stays open.
      if (stayedVisible && result.slot.revision === preview.revision + 1
        && result.slot.snapshot_digest === preview.snapshot_digest
        && (color === "white" ? result.slot.white_id : result.slot.black_id) === player.id) acceptLiveOffer(result.slot);
      navigate(`/play/slot/${result.slot.id}`);
    } catch (error) { message.textContent = failure(error); primary.disabled = false; sessionRecovery(root,error); }
    finally { document.removeEventListener("visibilitychange", visibility); }
  };
  return root;
}

export async function playSlotScreen(params: Params): Promise<HTMLElement> {
  // Read only on return: a missing old session must never become a replacement seat.
  const path = `/play/slot/${params.id}`;
  let player;
  try { player = await readPlayer(); } catch { return resourceRecovery(path); }
  if (!player) return resourceRecovery(path);

  let slot: Slot;
  try { slot = await req<Slot>("GET",`/api/play/slots/${encodeURIComponent(params.id)}`); }
  catch { return resourceRecovery(path); }
  remember(slot);
  if (slot.game_id) { navigate(`/game/${slot.game_id}`); return playPage(`<p>${t("hs.opening")}</p>`); }
  const root = handshakePage();
  root.querySelector("[data-more]")!.innerHTML = `<div class="invitation-share" hidden><label class="field"><span>${t("hs.link")}</span><input readonly aria-label="${t("hs.link")}" /></label>
      <button class="link-button" data-copy>Copy link</button></div><p class="fine" data-notes></p><p data-connection class="fine" role="status"></p>`;
  let intent: number | null = null; let busy = false; let loading = false; let dirty = false; let dead = false; let stopped = false;
  let consent = takeLiveOffer(slot);
  let lastConnection: string | null = null;
  let destroyReview = () => {};
  const message = root.querySelector<HTMLElement>(".msg")!;
  const primary = root.querySelector<HTMLButtonElement>("[data-primary]")!;
  const quiet = root.querySelector<HTMLElement>("[data-quiet]")!;
  const journal = new PlayJournal(slot.player_id,`slot:${slot.id}`);
  const connection = new PlayConnection(() => {
    if (lastConnection && connection.id !== lastConnection) { intent = null; consent = null; }
    lastConnection = connection.id;
    render(); void refresh();
  }, () => void refresh(), slot.id);
  const isHost = () => slot.player_id === slot.host.id;
  const side = (): "white" | "black" => slot.white_id ? (slot.player_id === slot.white_id ? "white" : "black") : slot.setup?.color ?? slot.setup?.transfer?.source.initiator_color ?? "white";
  const ownReady = () => isHost() ? slot.readiness.host : slot.readiness.recipient;
  const otherReady = () => isHost() ? slot.readiness.recipient : slot.readiness.host;
  const offeredInMessages = () => slot.setup?.transfer?.exporter?.build === "ios-messages-live-offer-v1";
  const appleMobile = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // The native app shares an import it owns; the browser shares a plain link.
  const nativeShare = (): string | null => {
    const source = slot.setup?.transfer?.source.match_id;
    return isHost() && slot.kind === "import" && appleMobile && source && /^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(source) ? `si.resonanca.hard.chess.api://share-live?id=${source}` : null;
  };
  const needsShare = () => !!slot.invite_url && !slot.recipient && !offeredInMessages() && slot.kind !== "match";
  async function share() {
    const app = nativeShare();
    if (app) { location.href = app; return; }
    if (navigator.share) {
      try { await navigator.share({title:"A game of chess",url:slot.invite_url}); return; }
      catch (error) { if (error instanceof DOMException && error.name === "AbortError") return; }
    }
    try { await copyText(slot.invite_url!); message.textContent = t("hs.copied"); }
    catch { message.textContent = t("hs.copyFallback"); }
  }
  function render() {
    const expired = slot.state === "expired" || slot.state === "cancelled";
    const host = isHost();
    const friend = host ? slot.recipient?.name : slot.host.name;
    const waitingOn = consent !== null || ownReady() || intent === slot.revision;
    root.classList.toggle("live-offer", slot.kind === "import");
    const who = expired ? t(slot.state === "expired" ? "hs.expired" : "hs.cancelled")
      : host ? (slot.recipient ? t(slot.presence?.recipient ? "hs.friendHere" : "waiting.opponentAway", {name: slot.recipient.name}) : t("hs.friendNotYet"))
      : t(slot.kind === "import" ? "hs.offerFrom" : "hs.invitedBy", {name: slot.host.name});
    root.querySelector("[data-line]")!.textContent = expired ? who : handshakeLine(who, side(), slot.terms);
    let status = "";
    primary.onclick = null; primary.disabled = busy || stopped; delete primary.dataset.share;
    quiet.replaceChildren();
    if (expired) {
      primary.textContent = t("hs.newInvitation"); primary.onclick = () => navigate("/");
    } else if (host && !slot.recipient) {
      status = offeredInMessages() ? t("hs.inMessages") : t("hs.shareHint");
      if (needsShare()) { primary.textContent = t("hs.sendToFriend"); primary.onclick = () => void share(); primary.dataset.share = nativeShare() ?? slot.invite_url ?? ""; }
      else { primary.textContent = t("hs.startGame"); primary.disabled = true; }
    } else if (waitingOn) {
      status = !slot.approved && !host ? t("hs.hostStarts", {name: slot.host.name}) : t("hs.youreIn", {name: friend ?? ""});
      primary.textContent = t("hs.waiting"); primary.disabled = true;
      quiet.append(quietAction(t("hs.holdOn"), () => {
        const wasReady = ownReady(); intent = null; consent = null;
        if (wasReady) void act({type:"withdraw_ready"}); else render();
      }));
    } else if (!slot.approved) {
      if (host) {
        status = t("hs.checkFriend");
        primary.textContent = t("hs.startGame"); primary.disabled ||= !connection.id;
        primary.onclick = () => { press(slot.id); consent = new LiveOfferConsent(slot); void act({type:"approve",recipient_id:slot.recipient?.id}); };
      } else {
        status = t("hs.hostStarts", {name: slot.host.name});
        primary.textContent = t("hs.waiting"); primary.disabled = true;
      }
    } else {
      const again = !host || pressed(slot.id);
      status = otherReady() ? t("hs.otherReady", {name: friend ?? ""}) : again ? t("hs.pressAgain") : "";
      primary.textContent = t(again ? "hs.imHere" : "hs.startGame"); primary.disabled ||= !slot.recipient || !connection.id;
      primary.onclick = () => { press(slot.id); void ready(); };
    }
    if (host && !expired && !slot.game_id && slot.kind !== "match") quiet.append(quietAction(t("hs.notNow"), () => void cancel()));
    if (!expired && slot.recipient && !slot.game_id) {
      const ownLater = host ? slot.later?.host : slot.later?.recipient;
      const otherLater = host ? slot.later?.recipient : slot.later?.host;
      if (ownLater) status = t("waiting.toldBusy");
      else if (otherLater) status = t("waiting.theyAreBusy", {name:friend ?? ""});
      if (!ownLater) quiet.append(quietAction(t("waiting.later"), () => { intent = null; consent = null; void act({type:"later"}); }));
    }
    root.querySelector("[data-status]")!.textContent = status;
    const shareBox = root.querySelector<HTMLElement>(".invitation-share")!;
    shareBox.hidden = expired || !needsShare() || nativeShare() !== null;
    if (slot.invite_url) shareBox.querySelector("input")!.value = slot.invite_url;
    const notes = expired ? [] : [termsLabel(slot.terms), ...(slot.kind === "import" ? [t("hs.messagesEnds")] : []), ...(slot.recipient ? [] : [expiry(slot.expires_at)])];
    root.querySelector("[data-notes]")!.textContent = notes.join(" · ");
    root.querySelector("[data-connection]")!.textContent = connection.error;
    if (root.isConnected) connection.seen("slot",slot.id,slot.revision);
  }
  async function refresh() {
    if (dead || stopped) return;
    if (loading) { dirty = true; return; }
    loading = true;
    try {
      const next = await req<Slot>("GET",`/api/play/slots/${slot.id}`);
      if (dead) return;
      if (next.revision !== slot.revision) intent = null;
      if (next.later?.host || next.later?.recipient) consent = null;
      if (consent && !consent.accepts(next)) consent = null;
      const changedPosition = next.snapshot_digest !== slot.snapshot_digest;
      slot = next;
      if (changedPosition) { destroyReview(); destroyReview = review(root,slot.setup,side()); }
      if (slot.game_id) { navigate(`/game/${slot.game_id}`); return; }
      render();
      if (!busy && (intent === slot.revision || consent !== null) && document.visibilityState === "visible" && connection.id && !ownReady() && slot.approved && slot.state === "paired") {
        consent = null; void ready();
      }
    } catch (error) { if (!dead) {
      message.textContent = failure(error); sessionRecovery(root,error);
      if (error instanceof ApiError && [401,403,404].includes(error.status)) {
        stopped = true; intent = null; consent = null; connection.close(); render();
      }
    } }
    finally { loading = false; if (dirty && !dead) { dirty = false; void refresh(); } }
  }
  async function act(action: Record<string,unknown>) {
    if (busy || dead || stopped) return;
    busy = true; render(); message.textContent = "";
    try {
      const result = await journal.send(`/api/play/slots/${slot.id}/commands`,{actor_id:slot.player_id,command_id:commandId(),expected_revision:slot.revision,action});
      if (result.outcome === "rejected") { message.textContent = outcomeMessage(result.code); intent = null; consent = null; }
    } catch (error) { message.textContent = `${failure(error)} ${t("hs.retryNote")}`; intent = null; consent = null; sessionRecovery(root,error); }
    finally { busy = false; render(); void refresh(); }
  }
  async function ready() {
    if (!connection.id || !slot.white_id || !slot.black_id) return;
    intent = slot.revision;
    await act({type:"ready",connection_id:connection.id,digest:slot.snapshot_digest,white_id:slot.white_id,black_id:slot.black_id});
  }
  async function cancel() {
    if (await confirmDialog(t("hs.cancelConfirm"), {danger:true})) {
      intent = null; consent = null; void act({type:"cancel"});
    }
  }
  root.querySelector<HTMLButtonElement>("[data-copy]")!.onclick = async () => {
    try { await copyText(slot.invite_url!); message.textContent = t("hs.copied"); }
    catch { message.textContent = t("hs.copyFallback"); }
  };
  render();
  mounted(root,() => {
    if (document.visibilityState !== "visible") { consent = null; intent = null; }
    destroyReview = review(root,slot.setup,side());
    connection.start(); const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); },3000);
    const visibility = () => {
      // Hiding the page takes back the yes. Coming back shows I'm here in place.
      if (document.visibilityState !== 'visible') { intent = null; consent = null; render(); }
      else void refresh();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { dead = true; clearInterval(timer); document.removeEventListener("visibilitychange", visibility); connection.close(); destroyReview(); };
  });
  return root;
}
